import autosize from 'autosize';
import ToggleLink from './components/toggleLink/toggleLink';
import Megadrop from './components/megadrop/megadrop';
import MobileMenu from './components/mobileMenu/mobileMenu';
import TabUsability from './components/tabUsability/tabUsability';
import Share from './components/share/share';
import 'flickity';
import 'flickity-imagesloaded';
import { wrap } from './components/utilities/utilities';
import { setCookie, getCookie } from './components/cookie/cookie';

/**
 * App
 * This starting point for this application.
 * @version 3.0.0
 * @exports App
 */
export default class App {
	constructor() {
		// Init menu
		new Megadrop();

		// Init mobile menu
		new MobileMenu(document.querySelector('[data-type="mobileMenu"]'), {
			ajaxPath: mainConfig.menuAjaxPath,
			ajaxActivePane: mainConfig.menuAjaxActivePane,
			searchField: false,
			searchMethod: mainConfig.searchMethod,
			searchPage: mainConfig.searchPage,
			searchParameter: mainConfig.searchParameter,
			searchPlaceholder: mainConfig.searchPlaceholder,
		});

		// Init toggle link
		new ToggleLink();

		// Init tabUsability
		new TabUsability();

		// Init share
		new Share();

		// Init table wrap for mobile scrolling of tables in rte.
		this._initRteTableWrap();

		// Init ajaxForm
		this._initAjaxForm();

		// Init cookies
		this._initCookies();

		// Init input
		this._initInput();

		// Init autosize
		this._initAutosize();

		// Init Fancybox
		this._initFancybox();

		// Init HideParagraphsWithEmptyAnchorTags
		this._initHideParagraphsWithEmptyAnchorTags();

		// Init HideParagraphsWithEmptyAnchorTags
		this._initTheCategoryDropdown();
	}

	/**
	 * Function that initiates the ajax-form functionality.
	 * @private
	 */
	_initAjaxForm() {
		let els = document.querySelectorAll('[data-type="ajaxForm"]');

		if (els.length > 0) {
			import(
				/* webpackChunkName: "ajaxForm" */ './components/ajaxForm/ajaxForm'
			).then(AjaxForm => {
				for (let i = 0; i < els.length; i++) {
					let el = els[i];

					if (el.getAttribute('data-ajaxform-initiated') === null) {
						new AjaxForm.default(el);
						el.setAttribute('data-ajaxform-initiated', true);
					}
				}
			});
		}
	}

	/**
	 * Function that wraps all tables in rte with a div to enable the scroll-feature in mobile.
	 * @private
	 */
	_initRteTableWrap() {
		let els = document.querySelectorAll('.rte table');

		els.forEach(el => {
			let wrapper = document.createElement('div');
			wrapper.classList.add('table-scrollable');
			wrap(el, wrapper);
		});
	}

	/**
	 * Initates cookies and cookiebar
	 * @private
	 */
	_initCookies() {
		const cookiebar = document.querySelector('.cookiebar');
		if (!getCookie('acceptCookieKarolinskaInstitutet')) {
			cookiebar.classList.remove('cookiebar--hidden');

			cookiebar
				.querySelector('.cookiebar__accept')
				.addEventListener('click', e => {
					e.preventDefault();
					setCookie('acceptCookieKarolinskaInstitutet', true, 10950, '/');
					cookiebar.classList.add('cookiebar--hidden');
				});
		}
	}

	/**
	 * Set classes on inputs that are filled.
	 * @private
	 */
	_initInput() {
		let els = document.querySelectorAll('.input__field');

		const onInputFocus = ev => {
			const btn = ev.target.form.querySelector('.btn');
			ev.target.parentNode.classList.add('input--filled');
			if (btn) {
				btn.classList.remove('btn--light');
			}
		};

		const onInputBlur = ev => {
			if (ev.target.value.trim() === '') {
				const btn = ev.target.form.querySelector('.btn');
				ev.target.parentNode.classList.remove('input--filled');
				if (btn) {
					btn.classList.add('btn--light');
				}
			}
		};

		els.forEach(el => {
			if (el.value.trim() !== '') {
				el.parentNode.classList.add('input--filled');
			}

			el.addEventListener('focus', onInputFocus);
			el.addEventListener('blur', onInputBlur);
		});
	}

	/**
	 * Initiate the autosize function on textareas.
	 * @private
	 */
	_initAutosize() {
		autosize(document.querySelectorAll('[data-type="autosize"]'));
	}

	_initFancybox() {
		/* global $ */
		import(/* webpackChunkName: "jquery" */ 'jquery').then(jquery => {
			window.$ = window.jQuery = jquery.default;
			import(/* webpackChunkName: "fancybox" */ '@fancyapps/fancybox').then(
				() => {
					$('.fancybox').fancybox({});
				}
			);
		});
	}

	/**
	 * This function removes the unnecessary spacing between elements that is added by the content editor in wordpress.
	 * @private
	 */
	_initHideParagraphsWithEmptyAnchorTags() {
		let paragraphs = document.querySelectorAll('p');

		paragraphs.forEach(paragraph => {
			let link = paragraph.getElementsByClassName('article__link')[0];

			if (link) {
				if (link.innerText === '') {
					paragraph.style.display = 'none';
				}
			}
		});
	}

	/**
	 * This function initializes the dropdowns on the Wordpress widget 'Categories'.
	 * @private
	 */
	_initTheCategoryDropdown() {
		let categories = document.querySelectorAll(
			'.block-category__list .block-category__item:not(.block-category__item__sub)'
		);

		categories.forEach(category => {
			let toggle = category.getElementsByClassName(
				'icon icon-chevron-right'
			)[0];

			if (toggle) {
				let usabillityElement = toggle.getElementsByTagName('span')[0];
				let categoryName = category.getElementsByClassName(
					'block-category__name'
				)[0].innerText;
				let subCategories = category.getElementsByClassName(
					'block-category__list__sub'
				)[0];

				toggle.addEventListener('click', () => {
					if (toggle.getAttribute('aria-expanded') === 'true') {
						close();
					} else {
						open();
					}
				});

				const open = () => {
					subCategories.style.display = 'block';
					toggle.setAttribute('aria-expanded', 'true');
					usabillityElement.innerHTML =
						"Dölj underkategorier för kategorin '" + categoryName + "'";
					toggle.classList.remove('icon-chevron-right');
					toggle.classList.add('icon-chevron-down');
				};

				const close = () => {
					subCategories.style.display = 'none';
					toggle.setAttribute('aria-expanded', 'false');
					usabillityElement.innerHTML =
						"Expandera underkategorier för kategorin '" + categoryName + "'";
					toggle.classList.remove('icon-chevron-down');
					toggle.classList.add('icon-chevron-right');
				};
			}
		});
	}
}
